import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { EMPTY, Subject } from 'rxjs';
import { catchError, exhaustMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';

enum ChildRegistrerd {
  YES_WITH_CEILING = 'Ja, op een school met leerlingplafond',
  YES_WITHOUT_CEILING = 'Ja, op een school zonder leerlingplafond',
  NO = 'Nee, mijn kind is niet aangemeld op een basisschool in Den Haag'
}

enum QuestionaireSubjects {
  INFORMATION_MUNICIPALITY = 'De ontvangen informatie vanuit de gemeente Den Haag; de 2- en 3-jarige ansichtkaart (inclusief aanmeldformulier)',
  INFORMATION_WEBSITE = 'Informatie op de website Scholenwijzer over het aanmeldproces',
  INFORMATION_SCHOOLS = 'Informatiebijeenkomsten op basisscholen',
  REGISTRATION_FORM = 'Het Aanmeldformulier',
  INFORMATION_DRAW = 'Informatie over loting',
  COMMUNICATION_FROM_APO = 'Communicatie over plaatsing vanuit AanmeldenPO',
  COMMUNICATION_FROM_SCHOOL = 'Communicatie vanuit de basisschool over inschrijving',
  INVITATION_EXTRA_DRAW = 'De uitnodiging voor de extra lotingsronde',
  SERVICEDESK = 'De hulp die ik ontvangen heb via de OuderServicedesk',
  REGISTRATION_HELP = 'De hulp bij de aanmelding met (mogelijk) extra ondersteuning',
  NVT = 'Niet van toepassing/geen van deze antwoorden',
  OTHER = 'Anders, namelijk…'
}

type Questionaire = {
  child_registrerd: ChildRegistrerd
  child_registrerd_explanation: string | null
  child_birth_month: string | null
  child_birth_year: string | null,
  satisfaction: number
  happy_about: QuestionaireSubjects[]
  happy_about_explanation: string | null
  unhappy_about: QuestionaireSubjects[]
  unhappy_about_explanation: string | null
  improvement_points: string | null
  captcha_token: string | null
}

type QuestionaireForm = {
  child_registrerd: FormControl<ChildRegistrerd | null>
  child_registrerd_explanation: FormControl<string | null>
  child_birth_month: FormControl<string | null>,
  child_birth_year: FormControl<string | null>
  satisfaction: FormControl<number | null>
  happy_about: FormArray<never>
  happy_about_explanation: FormControl<string | null>
  unhappy_about: FormArray<never>
  unhappy_about_explanation: FormControl<string | null>
  improvement_points: FormControl<string | null>
  captcha: FormControl
}

@Component({
  selector: 'app-apo-registration-process-questionaire',
  templateUrl: './apo-registration-process-questionaire.component.html',
  styleUrls: ['./apo-registration-process-questionaire.component.scss']
})
@UntilDestroy()
export class ApoRegistrationProcessQuestionaireComponent implements OnInit {
  @ViewChild('apoRegistrationProcessQuestionaire') apoRegistrationProcessQuestionaire: ElementRef;

  questionnaire = new FormGroup<QuestionaireForm>({
    child_registrerd: new FormControl(null, [Validators.required]),
    child_registrerd_explanation: new FormControl(null),
    child_birth_month: new FormControl(null, [Validators.required]),
    child_birth_year: new FormControl(null, [Validators.required]),
    satisfaction: new FormControl(null, [Validators.required, Validators.min(1), Validators.max(10)]),
    happy_about: new FormArray([], [this.atLeastOneSelectedValidator()]),
    happy_about_explanation: new FormControl(null, [Validators.maxLength(4000)]),
    unhappy_about: new FormArray([], [this.atLeastOneSelectedValidator()]),
    unhappy_about_explanation: new FormControl(null, [Validators.maxLength(4000)]),
    improvement_points: new FormControl(null),
    captcha: new FormControl(null, Validators.required),
  })

  ChildRegistrerd = ChildRegistrerd
  QuestionaireSubjects = QuestionaireSubjects

  private save$ = new Subject<Questionaire>();

  submitted : boolean = false;

  public months = [
    'Januari',
    'Februari',
    'Maart',
    'April',
    'Mei',
    'Juni',
    'Juli',
    'Augustus',
    'September',
    'Oktober',
    'November',
    'December',
  ]

  public years: number[] = []

  constructor(
      private http: HttpClient,
  ) {}

  ngOnInit(): void {
    const currentYear = new Date().getFullYear()
    this.years = Array.from({ length: 13 }, (_, i) => currentYear - i);

    this.initializeCheckboxArrays();

    this.questionnaire.controls.child_registrerd.valueChanges
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(value => {
        if (value === ChildRegistrerd.NO)
        {
          this.questionnaire.controls.child_registrerd_explanation.addValidators(Validators.required)
          this.questionnaire.controls.child_registrerd_explanation.updateValueAndValidity()
          return
        } 

        this.questionnaire.controls.child_registrerd_explanation.removeValidators(Validators.required)
        this.questionnaire.controls.child_registrerd_explanation.setValue(null)
        this.questionnaire.controls.child_registrerd_explanation.updateValueAndValidity()
      })

    this.questionnaire.controls.happy_about.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(values => this.questionaireSubjectsChanged(this.questionnaire.controls.happy_about_explanation, values))
    
    this.questionnaire.controls.unhappy_about.valueChanges.pipe(
        untilDestroyed(this)
    ).subscribe(values => this.questionaireSubjectsChanged(this.questionnaire.controls.unhappy_about_explanation, values))

    this.save$.pipe(
      exhaustMap(questionaire => {
        return this.http.post(`${environment.api}/apo-registration-questionaire`, questionaire, {
          responseType: 'json'
        })
        .pipe(
          catchError((err) => {
            alert('Er is een fout opgetreden bij het opslaan van de vragenlijst. Probeer het later opnieuw.');
            return EMPTY;
          })
        )
      }),
      untilDestroyed(this)
    ).subscribe(_ => {
      this.submitted = true
      this.apoRegistrationProcessQuestionaire.nativeElement.scrollIntoView({ behavior: 'instant', block: 'start' });
    })
  }

  private questionaireSubjectsChanged(control : FormControl<string | null>, values : boolean[])
  {
      const selected = this.getSelectedSubjectValues(values)

      if (selected.includes(QuestionaireSubjects.OTHER) && ! control.hasValidator(Validators.required)) 
      {
        control.addValidators(Validators.required)
        control.updateValueAndValidity()

        return
      } 

      if ( ! selected.includes(QuestionaireSubjects.OTHER) && control.hasValidator(Validators.required))
      {
        control.removeValidators(Validators.required)
        control.updateValueAndValidity()
      }
  }

  private initializeCheckboxArrays(): void {
    const happyAboutArray = this.questionnaire.get('happy_about') as FormArray;
    const unhappyAboutArray = this.questionnaire.get('unhappy_about') as FormArray;
    
    happyAboutArray.clear();
    unhappyAboutArray.clear();
    
    Object.values(QuestionaireSubjects).forEach(() => {
      happyAboutArray.push(new FormControl<boolean>(false));
      unhappyAboutArray.push(new FormControl<boolean>(false));
    });
  }

  private atLeastOneSelectedValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const formArray = control as FormArray;
      const hasSelection = formArray.controls.some(control => control.value === true);
      
      return hasSelection ? null : { atLeastOneRequired: true };
    };
  }

  getSelectedSubjectValues(inputArray : boolean[]): QuestionaireSubjects[] {
    const selectedValues: QuestionaireSubjects[] = [];
    
    inputArray.forEach((val, index) => {
      if (val) {
        selectedValues.push(Object.values(QuestionaireSubjects)[index]);
      }
    });

    return selectedValues;
  }

  onSubmit()
  {
    if ( ! this.questionnaire.valid) {

      this.questionnaire.markAllAsTouched();
      alert('Incorrecte invoer, controleer het formulier.')
      return
    }

    const formValue = this.questionnaire.value;

    const questionaireData: Questionaire = {
      child_registrerd: formValue.child_registrerd!,
      child_registrerd_explanation: formValue.child_registrerd_explanation ?? null,
      child_birth_month: formValue.child_birth_month ?? null,
      child_birth_year: formValue.child_birth_year ?? null,
      satisfaction: formValue.satisfaction!,
      happy_about: this.getSelectedSubjectValues(formValue.happy_about ?? []),
      happy_about_explanation: formValue.happy_about_explanation ?? null,
      unhappy_about: this.getSelectedSubjectValues(formValue.unhappy_about ?? []),
      unhappy_about_explanation: formValue.unhappy_about_explanation ?? null,
      improvement_points: formValue.improvement_points ?? null,
      captcha_token: formValue.captcha ?? null
    };

    this.save$.next(questionaireData)
  }

  objectValues = (obj : object) => Object.values(obj) 
}
